import { Component, EventEmitter, inject, Input, OnInit, Output, signal, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { JjamesButtonComponent, JJamesInputComponent, ResponsiveBaseComponent, JJamesCardComponent, JJamesAlertComponent } from "shared-lib";
import { EditMyAccountRequest, ParkingOperatorDto, ParkingService, AddImageToParkingOperatorRequest, ImageDto } from '../../../api/gen';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-general-account',
  imports: [JjamesButtonComponent, JJamesInputComponent, JJamesCardComponent, ReactiveFormsModule, JJamesAlertComponent, MatSnackBarModule, MatProgressSpinnerModule, TranslatePipe],
  templateUrl: './general-account.component.html',
  styleUrl: './general-account.component.scss'
})
export class GeneralAccountComponent extends ResponsiveBaseComponent implements OnInit, OnChanges {

  private formBuilder = inject(FormBuilder);
  private parkingService = inject(ParkingService);
  private snackBar = inject(MatSnackBar);

  @ViewChild('photoInput') photoInput!: ElementRef<HTMLInputElement>;

  @Output() editFormSubmitted = new EventEmitter<EditMyAccountRequest>();
  @Output() photoUploaded = new EventEmitter<ImageDto>();
  @Input() accountData: ParkingOperatorDto | null = null;

  editForm: FormGroup;
  isUploadingPhoto = signal(false);
  isLoadingPhoto = signal(false);
  profileImages = signal<ImageDto[]>([]);

  editAccount() {
    console.log('Edit account form submitted:', this.editForm.value);
    if (this.editForm.valid) {

      const updatedAccount: EditMyAccountRequest = {
        firstName: this.editForm.value.firstName,
        lastName: this.editForm.value.lastName,
        email: this.editForm.value.email
      };
      this.editFormSubmitted.emit(updatedAccount);
    }
    else {
      this.markFormGroupTouched(this.editForm);
    }
  }
  constructor() {
    super();
    // Initialize form here if needed
    this.editForm = this.formBuilder.group({
      firstName: [this.accountData?.firstName ?? '', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      lastName: [this.accountData?.lastName ?? '', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      email: [this.accountData?.email ?? '', [Validators.required, Validators.email]]
    });
  }
  ngOnInit(): void {
    this.fillFormData();
    this.loadProfilePhoto();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['accountData'] && !changes['accountData'].firstChange && this.accountData?.id) {
      this.loadProfilePhoto();
    }
  }

  private loadProfilePhoto(): void {
    if (!this.accountData?.id) {
      return;
    }

    this.isLoadingPhoto.set(true);
    this.parkingService.parkingOperatorParkingOperatorIdImagesGet(this.accountData.id).subscribe({
      next: (images) => {
        this.profileImages.set(images);
        this.isLoadingPhoto.set(false);
      },
      error: (error) => {
        console.error('Error loading profile photo:', error);
        this.profileImages.set([]);
        this.isLoadingPhoto.set(false);
      }
    });
  }

  getProfilePhoto(): string | undefined {
    const images = this.profileImages();
    const primaryImage = images.find(img => img.isPrimary);
    if (primaryImage?.fileUrl) {
      return primaryImage.fileUrl;
    }
    return images[0]?.fileUrl ?? undefined;
  }

  onPhotoClick(): void {
    this.photoInput?.nativeElement?.click();
  }

  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file || !this.accountData?.id) {
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.snackBar.open('Please select a valid image file (JPEG, PNG, GIF, or WebP)', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.snackBar.open('Image file size must be less than 5MB', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.uploadPhoto(file);
  }

  private uploadPhoto(file: File): void {
    this.isUploadingPhoto.set(true);

    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result as string;
      const base64Data = base64String.split(',')[1];

      const request: AddImageToParkingOperatorRequest = {
        fileName: file.name,
        base64Data: base64Data,
        isPrimary: true
      };

      this.parkingService.parkingOperatorParkingOperatorIdImagesPost(this.accountData!.id!, request).subscribe({
        next: (imageDto) => {
          this.isUploadingPhoto.set(false);
          this.snackBar.open('Profile photo uploaded successfully', 'Close', {
            duration: 3000
          });
          // Update the local images array
          this.profileImages.update(images => [imageDto, ...images.filter(img => !img.isPrimary)]);
          this.photoUploaded.emit(imageDto);
          // Reset file input
          if (this.photoInput?.nativeElement) {
            this.photoInput.nativeElement.value = '';
          }
        },
        error: (error) => {
          this.isUploadingPhoto.set(false);
          console.error('Error uploading photo:', error);
          this.snackBar.open('Failed to upload photo. Please try again.', 'Close', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
        }
      });
    };

    reader.onerror = () => {
      this.isUploadingPhoto.set(false);
      this.snackBar.open('Failed to read image file', 'Close', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
    };

    reader.readAsDataURL(file);
  }

  private fillFormData(): void {
    // wait the accountData to be available

    if (this.accountData) {
      this.editForm.patchValue({
        firstName: this.accountData.firstName,
        lastName: this.accountData.lastName,
        email: this.accountData.email
      });
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }
  getFieldError(fieldName: string): string {
    const field = this.editForm.get(fieldName);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) return `${this.getFieldLabel(fieldName)} is required`;
      if (field.errors['minlength']) return `${this.getFieldLabel(fieldName)} is too short`;
      if (field.errors['maxlength']) return `${this.getFieldLabel(fieldName)} is too long`;
      if (field.errors['min']) return `${this.getFieldLabel(fieldName)} value is too low`;
      if (field.errors['max']) return `${this.getFieldLabel(fieldName)} value is too high`;
    }
    return '';
  }
  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email Address',
    };
    return labels[fieldName] || fieldName;
  }
  isFieldInvalid(fieldName: string): boolean {
    const field = this.editForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

}
